import { useMemo, useState } from "react";
import { Card, Common, CustomScrollbar, Icon } from "@/components";
import clsx from "clsx";
import { manifest, manifestLine, solution } from "../models";

export interface CrossZoneManifestLine {
  manifestLine: manifestLine;
  originalManifest: manifest;
  newManifest: manifest;
  defaultZone: string;
  targetManifest: manifest | undefined; // The manifest to route to when rejected (based on setting)
  status: "pending" | "accepted" | "rejected";
}

interface Props {
  manifests: manifest[];
  solutions: solution[];
  crossZoneLines: CrossZoneManifestLine[];
  isReoptimizing: boolean;
  isSaving: boolean;
  reoptimizingRoutesCount: number;
  isByTagMode: boolean; // routeOptimisationAcceptRejectSetting === 1
  onAcceptNew: (lineId: string) => void;
  onKeepOriginal: (lineId: string) => void;
  onUndo: (lineId: string) => void;
  onBulkAcceptNew: (lineIds: string[]) => void;
  onBulkKeepOriginal: (lineIds: string[]) => void;
  onBack: () => void;
  onSaveAll: () => void;
}

export const ModalOptimizedCherryPick = ({
  manifests,
  solutions,
  crossZoneLines,
  isReoptimizing,
  isSaving,
  reoptimizingRoutesCount,
  isByTagMode,
  onAcceptNew,
  onKeepOriginal,
  onUndo,
  onBulkAcceptNew,
  onBulkKeepOriginal,
  onBack,
  onSaveAll,
}: Props) => {
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);

  // Get button text based on state
  const saveButtonText = useMemo(() => {
    if (isReoptimizing) {
      return `Reoptimizing ${reoptimizingRoutesCount} Route${
        reoptimizingRoutesCount > 1 ? "s" : ""
      }`;
    }
    if (isSaving) {
      return "Saving";
    }
    return "Save All";
  }, [isReoptimizing, isSaving, reoptimizingRoutesCount]);

  // Check if all records have been decided (no pending)
  const allDecided = useMemo(() => {
    return crossZoneLines.every((line) => line.status !== "pending");
  }, [crossZoneLines]);

  // Check if all checkboxes are selected
  const allSelected = useMemo(() => {
    return (
      crossZoneLines.length > 0 &&
      selectedLineIds.length === crossZoneLines.length
    );
  }, [selectedLineIds, crossZoneLines]);

  // Handle individual checkbox toggle
  const handleToggleCheckbox = (lineId: string) => {
    setSelectedLineIds((prev) =>
      prev.includes(lineId)
        ? prev.filter((id) => id !== lineId)
        : [...prev, lineId]
    );
  };

  // Handle select all checkbox toggle
  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(crossZoneLines.map((line) => line.manifestLine.id));
    }
  };

  // Handle bulk accept
  const handleBulkAcceptNew = () => {
    if (selectedLineIds.length > 0) {
      onBulkAcceptNew(selectedLineIds);
      setSelectedLineIds([]);
    }
  };

  // Handle bulk reject
  const handleBulkKeepOriginal = () => {
    if (selectedLineIds.length > 0) {
      onBulkKeepOriginal(selectedLineIds);
      setSelectedLineIds([]);
    }
  };

  // Get tag summary data (for by tag mode)
  const tagSummaryData = useMemo(() => {
    if (!isByTagMode) return [];

    // Collect all manifest lines with delivery tags and group by default tag
    const tagGroups = new Map<
      string,
      {
        tag: string;
        firstRoute: manifest | undefined;
        originalCount: number;
        adjustedCount: number;
      }
    >();

    // Collect all manifest lines from all manifests and their solutions
    manifests.forEach((manifest) => {
      // Process original manifest lines
      manifest.manifestLines.forEach((line) => {
        if (line.deliveryTags && line.deliveryTags.length > 0) {
          const defaultTag = line.deliveryTags[0];

          if (!tagGroups.has(defaultTag)) {
            // Find first route with this tag (sorted by name)
            const routesWithTag = manifests
              .filter((m) => m.deliveryTags?.includes(defaultTag))
              .sort((a, b) => a.route.name.localeCompare(b.route.name));

            tagGroups.set(defaultTag, {
              tag: defaultTag,
              firstRoute: routesWithTag[0] || undefined,
              originalCount: 0,
              adjustedCount: 0,
            });
          }

          const group = tagGroups.get(defaultTag)!;
          group.originalCount++;
        }
      });
    });

    manifests.forEach((manifest) => {
      const deliveryTags = manifest.deliveryTags || [];
      if (
        deliveryTags.length &&
        deliveryTags.every((tag) => !tagGroups.has(tag))
      ) {
        deliveryTags.forEach((tag) => {
          tagGroups.set(tag, {
            tag,
            firstRoute: manifest,
            originalCount: 0,
            adjustedCount: 0,
          });
        });
      }
    });

    // calculate adjusted count
    tagGroups.forEach((group) => {
      const visits = solutions
        .flatMap((slt) => slt.visits)
        .filter(
          (visit) =>
            visit.manifestLine.deliveryTags?.length &&
            visit.manifestLine.deliveryTags[0] === group.tag
        );
      group.adjustedCount = visits.length;
    });

    // Adjust counts based on rejected decisions
    crossZoneLines.forEach((czLine) => {
      if (
        czLine.status === "rejected" &&
        czLine.manifestLine.deliveryTags?.length
      ) {
        const defaultTag = czLine.manifestLine.deliveryTags[0];
        // get new route and tag from new manifest
        const newGroup = tagGroups.get(defaultTag);
        if (newGroup && !newGroup?.firstRoute) {
          newGroup.adjustedCount--;
        }
      }
    });

    // Sort by tag: numeric tags first (sorted by number), then non-numeric tags (sorted alphabetically)
    const allTags = Array.from(tagGroups.values());

    // Separate numeric and non-numeric tags
    const numericTags = allTags.filter((row) => !isNaN(Number(row.tag)));
    const nonNumericTags = allTags.filter((row) => isNaN(Number(row.tag)));

    // Sort numeric tags by numeric value
    numericTags.sort((a, b) => Number(a.tag) - Number(b.tag));

    // Sort non-numeric tags alphabetically
    nonNumericTags.sort((a, b) => a.tag.localeCompare(b.tag));

    // Combine: numeric tags first, then non-numeric tags
    return [...numericTags, ...nonNumericTags];
  }, [manifests, solutions, crossZoneLines, isByTagMode]);

  // Get route summary data (for by route mode)
  const routeSummaryData = useMemo(() => {
    if (isByTagMode) return [];

    return manifests.map((manifest) => {
      const originalCount = manifest.manifestLines.length;
      const slt = solutions.find((s) => s.manifestId === manifest.id);
      const optimizedCount = slt?.visits.length || 0;

      // Calculate adjusted count based on accepted/rejected decisions
      let adjustedCount = optimizedCount;

      crossZoneLines.forEach((czLine) => {
        if (czLine.status === "rejected") {
          // Line is being moved from new manifest to target manifest
          if (czLine.newManifest.id === manifest.id) {
            adjustedCount--;
          }
          if (czLine.targetManifest?.id === manifest.id) {
            adjustedCount++;
          }
        }
      });

      return {
        manifest,
        originalCount,
        optimizedCount,
        adjustedCount,
      };
    });
  }, [manifests, solutions, crossZoneLines, isByTagMode]);

  return (
    <Card title="Cherry-pick Optimization Result">
      <div className="flex space-x-4 p-4 h-[calc(100vh-11.625rem)]">
        <div className="w-full flex space-x-4">
          {/* Summary Table - by Tag or by Route */}
          <div className="w-1/3">
            <h3 className="text-base font-semibold mb-2 h-7">
              {isByTagMode ? "Tag Summary Table" : "Route Summary Table"}
            </h3>
            <div className="h-[calc(100%-44px)] border border-line">
              <CustomScrollbar>
                <table className="w-full">
                  <thead className="sticky top-0 z-[1]">
                    <tr>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        {isByTagMode ? "Tag" : "Delivery Tag"}
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        Route
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
                        Original No. Orders
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
                        New No. Orders
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isByTagMode
                      ? // By Tag Mode
                        tagSummaryData.map((row) => (
                          <tr key={row.tag} className="hover:bg-neutral-5">
                            <td className="px-4 py-2 border-b border-neutral-10">
                              {row.tag}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10">
                              {row.firstRoute?.route?.name || "-"}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10 text-center">
                              {row.originalCount}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10">
                              <div className="flex items-center justify-center">
                                <div className="relative">
                                  <span>{row.adjustedCount}</span>
                                  {row.adjustedCount > row.originalCount && (
                                    <Icon.ArrowUp className="w-4 h-4 fill-green absolute left-full ml-1 top-1/2 -translate-y-1/2" />
                                  )}
                                  {row.adjustedCount < row.originalCount && (
                                    <Icon.ArrowDown className="w-4 h-4 fill-red absolute left-full ml-1 top-1/2 -translate-y-1/2" />
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))
                      : // By Route Mode
                        routeSummaryData.map((row) => (
                          <tr
                            key={row.manifest.id}
                            className="hover:bg-neutral-5"
                          >
                            <td className="px-4 py-2 border-b border-neutral-10">
                              {row.manifest.deliveryTags?.join(", ") || "-"}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10">
                              {row.manifest.route.name}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10 text-center">
                              {row.originalCount}
                            </td>
                            <td className="px-4 py-2 border-b border-neutral-10">
                              <div className="flex items-center justify-center">
                                <div className="relative">
                                  <span>{row.adjustedCount}</span>
                                  {row.adjustedCount > row.originalCount && (
                                    <Icon.ArrowUp className="w-4 h-4 fill-green absolute left-full ml-1 top-1/2 -translate-y-1/2" />
                                  )}
                                  {row.adjustedCount < row.originalCount && (
                                    <Icon.ArrowDown className="w-4 h-4 fill-red absolute left-full ml-1 top-1/2 -translate-y-1/2" />
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </CustomScrollbar>
            </div>
          </div>

          {/* Accept/Reject Table */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold h-7">
                Accept / Reject Table
              </h3>
              {selectedLineIds.length > 0 && (
                <div className="flex space-x-2">
                  <Common.Button
                    onClick={handleBulkAcceptNew}
                    color="green"
                    size="sm"
                  >
                    Accept {selectedLineIds.length} Selected
                  </Common.Button>
                  <Common.Button
                    onClick={handleBulkKeepOriginal}
                    outline
                    color="gray"
                    size="sm"
                  >
                    Keep Original {selectedLineIds.length} Selected
                  </Common.Button>
                </div>
              )}
            </div>
            <div className="h-[calc(100%-44px)] border border-line">
              <CustomScrollbar>
                <table className="w-full">
                  <thead className="sticky top-0 z-[1]">
                    <tr>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center w-12">
                        <div className="flex justify-center">
                          <Common.Checkbox
                            id="select-all-checkbox"
                            checked={allSelected}
                            onChange={handleToggleSelectAll}
                            ipSize="md"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        Original Tag
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        Customer Order
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        Default Route / Tag
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        New Route
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                        Address
                      </th>
                      <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {crossZoneLines.map((line) => {
                      const doc =
                        line.manifestLine.document ||
                        line.manifestLine.invoice ||
                        line.manifestLine.order;
                      const customerName =
                        doc?.customerTradingName || "Unknown";
                      const orderNumber = doc?.saleDocumentNumber || "N/A";
                      const address = [
                        doc?.deliveryAddressLine1,
                        doc?.deliveryAddressLine2,
                        doc?.deliverySuburb,
                        doc?.deliveryState,
                        doc?.deliveryPostCode,
                      ]
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <tr
                          key={line.manifestLine.id}
                          className="hover:bg-neutral-5"
                        >
                          <td className="px-4 py-2 border-b border-neutral-10 text-center">
                            <div className="flex justify-center">
                              <Common.Checkbox
                                id={`checkbox-${line.manifestLine.id}`}
                                checked={selectedLineIds.includes(
                                  line.manifestLine.id
                                )}
                                onChange={() =>
                                  handleToggleCheckbox(line.manifestLine.id)
                                }
                                ipSize="md"
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            {line.defaultZone}
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            <span className="text-blue whitespace-nowrap font-semibold">
                              #{orderNumber} -{" "}
                            </span>
                            <span className="font-semibold">
                              {customerName}
                            </span>
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            {line.targetManifest?.route.name || "-"}
                            {line.targetManifest?.deliveryTags?.length && (
                              <span className="text-neutral-40">
                                (
                                {line.targetManifest?.deliveryTags?.join(
                                  ", "
                                ) || "-"}
                                )
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            {line.newManifest.route.name} (
                            {line.newManifest.deliveryTags?.join(", ") || "-"})
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            {address}
                          </td>
                          <td className="px-4 py-2 border-b border-neutral-10">
                            <div className="flex justify-center space-x-2">
                              {line.status === "pending" && (
                                <>
                                  <Common.Button
                                    onClick={() =>
                                      onAcceptNew(line.manifestLine.id)
                                    }
                                    color="green"
                                    size="sm"
                                  >
                                    Accept
                                  </Common.Button>
                                  <Common.Button
                                    onClick={() =>
                                      onKeepOriginal(line.manifestLine.id)
                                    }
                                    outline
                                    color="gray"
                                    size="sm"
                                  >
                                    Keep
                                  </Common.Button>
                                </>
                              )}
                              {line.status !== "pending" && (
                                <div className="flex items-center space-x-2">
                                  <span
                                    className={clsx(
                                      "text-sm font-semibold",
                                      line.status === "accepted"
                                        ? "text-green"
                                        : "text-red"
                                    )}
                                  >
                                    {line.status === "accepted"
                                      ? "Accepted"
                                      : "Rejected"}
                                  </span>
                                  <Common.Button
                                    onClick={() => onUndo(line.manifestLine.id)}
                                    color="blue"
                                    size="sm"
                                    outline
                                  >
                                    Undo
                                  </Common.Button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CustomScrollbar>
            </div>
          </div>
        </div>
      </div>
      <div className="shadow-overline px-4 py-3 flex space-x-2 items-center">
        <div className="flex-1"></div>
        <Common.Button
          onClick={onBack}
          outline
          color="gray"
          disabled={isReoptimizing}
        >
          Back
        </Common.Button>
        <Common.Button
          onClick={onSaveAll}
          isLoading={isReoptimizing || isSaving}
          disabled={isReoptimizing || isSaving || !allDecided}
        >
          {saveButtonText}
        </Common.Button>
      </div>
    </Card>
  );
};
